import { captureMessage } from '@sentry/angular';
import { Dto, logger } from '@damofire/data-commons';
import { proxy, releaseProxy, Remote } from 'comlink';
import { clone } from 'lodash-es';
import { Observer, Subject } from 'rxjs';
import { OPERATION } from '../../des.dto';
import { WarehouseSyncStepContextEvent, WarehouseSyncStepTaskContext } from '../executors';
import type { ProgressStats } from '../executors';
import { AbstractWarehouseSyncStepWorker } from '../workers/abstract-warehouse-worker.utils';

export interface WarehouseSyncStepWorkerFactory<W> {
  createWorker: (environment: any) => Promise<[Worker, W | Remote<W>]>;
}

const emitTaskProgress = (
  ctx: WarehouseSyncStepTaskContext,
  listener: Observer<WarehouseSyncStepContextEvent>,
  isCancelled: () => boolean,
  progress: number,
  stats?: ProgressStats,
): void => {
  ctx.progress = progress;
  if (stats?.processed !== undefined) {
    ctx.processed = stats.processed;
  }
  if (stats?.expected !== undefined) {
    ctx.expected = stats.expected;
  }
  if (!isCancelled()) {
    ctx.when = Date.now();
    listener.next({ ctx: clone(ctx), event: 'TASK_PROGRESSED' });
  }
};

export const executeWorker = async <W extends AbstractWarehouseSyncStepWorker<T>, T extends Dto<string> = Dto<string>>(
  ctx: WarehouseSyncStepTaskContext,
  environment: any,
  listener: Observer<WarehouseSyncStepContextEvent>,
  workerFactory: WarehouseSyncStepWorkerFactory<W>,
  taskBreaker: Subject<boolean>,
  itemsProcessedCallback: (change: { data: T; operation: OPERATION }, buffer: ArrayBuffer) => void = () => ({}),
): Promise<void> => {
  // Wrap dans une promise, lorsque taskBreak débloque, il semble que le execute ne débarre pas.
  return new Promise((resolve, reject) => {
    workerFactory.createWorker(environment).then(async ([warehouseWorker, workerService]) => {
      let cancelled = false;
      const sub = taskBreaker.subscribe(async () => {
        logger.info('Task cancel received', { category: 'sync', step: ctx.step, task: ctx.task });
        await workerService.cancel();
        cancelled = true;
      });

      try {
        await retry(async () => {
          const progressCb = (progress: number, stats?: ProgressStats): void => emitTaskProgress(ctx, listener, () => cancelled, progress, stats);

          if (environment.appConfig.watson.useWebworkers) {
            await workerService.execute(
              environment,
              ctx,
              proxy((progress: number, stats?: ProgressStats) => emitTaskProgress(ctx, listener, () => cancelled, progress, stats)),
              proxy((change: { data: T; operation: OPERATION }, buffer: ArrayBuffer) => itemsProcessedCallback(change, buffer)),
            );
          } else {
            await workerService.execute(environment, ctx, progressCb, itemsProcessedCallback);
          }
        }, ctx);
        resolve();
      } catch (err) {
        logger.error(`Worker failed: ${ctx.step} - ${ctx.task}`, { category: 'sync' });
        captureMessage(`executeWorker - Cannot run ${ctx.step} - ${ctx.task} worker.`, {
          level: 'error',
          extra: { err: err || 'no error object' },
        });
        reject(err);
      } finally {
        sub.unsubscribe();

        if (environment.appConfig.watson.useWebworkers) {
          (workerService as Remote<W>)[releaseProxy]();
          warehouseWorker.terminate();
        }
      }
    }, reject);
  });
};

export const retry = (fn: () => Promise<unknown>, ctx: WarehouseSyncStepTaskContext, retries = 3, err?: unknown): Promise<unknown> => {
  if (!retries) {
    logger.warn('Retries exhausted', { category: 'sync', step: ctx.step, task: ctx.task });
    return Promise.reject(err);
  }
  return fn().catch((err) => {
    logger.debug('Attempting retry', { category: 'sync', step: ctx.step, task: ctx.task });
    return retry(fn, ctx, retries - 1, err);
  });
};

export const getErrorMessage = (err: string | Record<string, string>): string => {
  if (err) {
    if (typeof err === 'string') {
      return err;
    }

    if ('message' in err) {
      return err.message;
    }
  }
  return 'no error message';
};
